import React, { useMemo, useState } from "react";
import { Block } from "@randajan/jet-react/dom/block";
import { Alert, Box, TextField, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { validateProfileForm } from "../../../../../arc/forms/profileForms.js";
import { Say } from "../../../../../arc/locale/consts.js";
import { meBeam } from "../../../../assets/beams/meBeam";
import { BlimpMuiFormActions } from "../../../../elements/BlimpMuiFormActions/BlimpMuiFormActions";
import { BlimpMuiFormSection } from "../../../../elements/BlimpMuiFormSection/BlimpMuiFormSection";
import { Locale } from "../../../../assets/Locale.js";
import { useBeforeUnload } from "../../../../assets/hooks/useBeforeUnload.jsx";
import { getProfileErrorId, getValidationFieldError, handleProfileSubmitReply, mergeValidation } from "../profileValidation.js";

import "./PinForm.scss";

const emptyValues = Object.freeze({
    pin:"",
    pinConfirm:""
});

const pinFields = Object.freeze([
    { name:"pin", autoComplete:"new-password" },
    { name:"pinConfirm", autoComplete:"new-password" }
]);

const pinFieldNames = Object.freeze(pinFields.map(field=>field.name));
const touchAllFields = _=>Object.fromEntries(pinFieldNames.map(field=>[field, true]));

export const PinForm = (props)=>{
    const { profile, onCancel, onSaved } = props;
    const say = Locale.use();
    const [ values, setValues ] = useState(emptyValues);
    const [ touched, setTouched ] = useState({});
    const [ submitState, setSubmitState ] = useState("");
    const [ serverValidation, setServerValidation ] = useState(null);
    const [ isSubmitting, setIsSubmitting ] = useState(false);

    useBeforeUnload();

    const localValidation = useMemo(()=>validateProfileForm("pin", values), [values]);
    const validation = useMemo(()=>mergeValidation(localValidation, serverValidation), [localValidation, serverValidation]);
    const showAllErrors = submitState === "validationError";

    const setFieldValue = event=>{
        const { name, value } = event.target;
        setValues(current=>({ ...current, [name]:value }));
        setServerValidation(null);
        setSubmitState("");
    };

    const touchField = event=>{
        setTouched(current=>({ ...current, [event.target.name]:true }));
    };

    const submit = async event=>{
        event.preventDefault();
        const result = validateProfileForm("pin", values);
        if (!result.isValid) {
            setServerValidation(null);
            setTouched(touchAllFields());
            setSubmitState("validationError");
            return;
        }
        setIsSubmitting(true);
        try {
            const reply = await meBeam.act.updatePin(result.values.pin);
            handleProfileSubmitReply(reply, {
                fields:pinFieldNames,
                setIsSubmitting,
                setServerValidation,
                setSubmitState,
                setTouched,
                onDone:onSaved
            })
        } catch(err) {
            setSubmitState("submitError");
            setIsSubmitting(false);
        }
    };

    return (
        <Block level={0} className="PinForm popScreen" caption={say(profile?.hasPin ? "profilePinPopTitleChange" : "profilePinPopTitleSet")}>
            <Box className="BlimpMuiForm" component="form" onSubmit={submit} noValidate>
                <Typography className="BlimpMuiForm-intro">
                    {say("profilePinIntro")}
                </Typography>
                {submitState === "validationError" && (
                    <Alert severity="error">{say("formErrorHighlightedFields")}</Alert>
                )}
                {submitState === "submitError" && (
                    <Alert severity="error">{say("profilePinErrorSubmit")}</Alert>
                )}
                <Box className="BlimpMuiForm-sections">
                    <BlimpMuiFormSection icon={LockOutlinedIcon} title={say("profilePinFieldPin")}>
                        {pinFields.map(field=>{
                            const fieldError = getValidationFieldError(validation, field.name);
                            const showError = !!fieldError && (showAllErrors || touched[field.name]);
                            return (
                                <TextField
                                    autoComplete={field.autoComplete}
                                    className="BlimpMuiForm-field"
                                    error={showError}
                                    fullWidth
                                    helperText={showError ? say(getProfileErrorId(field.name, fieldError)) : " "}
                                    key={field.name}
                                    label={say(Say.id("profile", "pin", "field", field.name))}
                                    name={field.name}
                                    onBlur={touchField}
                                    onChange={setFieldValue}
                                    required
                                    slotProps={{ htmlInput:{ inputMode:"numeric", maxLength:8, pattern:"[0-9]*" } }}
                                    type="password"
                                    value={values[field.name]}
                                    variant="outlined"
                                />
                            );
                        })}
                    </BlimpMuiFormSection>
                </Box>
                <BlimpMuiFormActions
                    onCancel={onCancel}
                    submitDisabled={isSubmitting}
                    submitLabel={isSubmitting ? say("commonSaving") : say("profilePinSubmit")}
                />
            </Box>
        </Block>
    );
};
